import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useReactFlow } from "reactflow";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import LoadingOverlay from "../mui/LoadingOverlay";
import ResetChartModal from "./ResetchartModal";
import WarningModal from "../components/WarningModal";
import { selectisEdit, setIsEdit } from "../redux/nodePositionedSlice";
import { orgwarndata } from "../redux/websocketSlice";
import { selectAdmin, selectDevice } from "../redux/adminSlice";
import { seteditedNewNodes } from "../redux/nodesSlice";
import { useUpdatePositionsMutation } from "../redux/organizationApi";
import { useHydrateHierarchy } from "../hooks/useHydrateHierarchy";

const D3Edit = lazy(() =>
  import("./D3_Edit").then((module) => ({ default: module.D3edit }))
);
import React from "react";
import { D3_View } from "./D3_View";

function FullLayoutComponent() {
  // Memoize container style to prevent re-renders
  const containerStyle = useMemo(
    () => ({
      width: "100%",
      height: "100%",
      backgroundColor: "#D9D9D94D",
      overflow: "hidden",
      position: "relative",
    }),
    []
  );
  useHydrateHierarchy();
  const deviceId = useSelector(selectDevice);
  const admin = useSelector(selectAdmin);

  const isEdit = useSelector(selectisEdit);
  const warndata = useSelector(orgwarndata);
  // const editedNewNodes = useSelector(selectnodess);
  const [data, setdata] = useState();
  const [open, setOpen] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  // used to tell D3_View when to auto-fit (after edit save / reset)
  const [fitOnMountKey, setFitOnMountKey] = useState(0);
  const dispatch = useDispatch();
  const reactFlowInstance = useReactFlow();
  const [updatePositions, { isLoading: isPositionsUpdating }] =
    useUpdatePositionsMutation();

  // Use ref to track if component is mounted (prevents memory leaks from async operations)
  const isMountedRef = useRef(true);
  // Track timeouts for cleanup
  const timeoutRefs = useRef([]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cleanup all pending timeouts
      timeoutRefs.current.forEach((timeoutId) => {
        if (timeoutId) clearTimeout(timeoutId);
      });
      timeoutRefs.current = [];
      dispatch({
        type: "alertremove",
        payload: {
          id: "Organisationalchart",
          message: "Another users also edit this business group",
        },
      });
    };
  }, [dispatch]);

  const getCurrentNodes = useCallback(() => {
    if (!isMountedRef.current) return [];
    const nodes = reactFlowInstance.getNodes?.() || [];
    return nodes;
  }, [reactFlowInstance]);

  const position = useCallback(
    async (orgs, permission) => {
      if (!orgs || orgs.length === 0 || !isMountedRef.current) {
        return;
      }
      setOpen(true);
      try {
        await updatePositions(orgs).unwrap();
        // Check if component is still mounted before updating state
        if (!isMountedRef.current) return;

        // if (updatedData?.updatedNodes) {
        //   dispatch(seteditedNewNodes(updatedData.updatedNodes));
        // }

        toast.success("Chart updated successfully");
        if (permission === true) {
          dispatch(setIsEdit(!isEdit));
          // coming back from edit: ask D3_View to auto-fit once
          setFitOnMountKey((k) => k + 1);
          dispatch({
            type: "alertremove",
            payload: {
              id: "Organisationalchart",
              message: "Another users also edit this business group",
            },
          });
        }
      } catch (error) {
        if (!isMountedRef.current) return;
        const message =
          error?.data?.message ||
          error?.error ||
          "Permission denied while updating chart";
        toast.error(message);
        dispatch(setIsEdit(!isEdit));
      } finally {
        if (isMountedRef.current) {
          setOpen(false);
        }
      }
    },
    [dispatch, isEdit, updatePositions]
  );

  const resetchart = useCallback(async () => {
    if (!isMountedRef.current) return;
    const updatedNodes = getCurrentNodes().map((node) => ({
      id: node.id,
      position: null,
    }));
    await position(updatedNodes, false);
    // after reset, also auto-fit once
    setFitOnMountKey((k) => k + 1);
  }, [getCurrentNodes, position]);

  // Memoize button click handlers to prevent unnecessary re-renders
  const handleEditSaveClick = useCallback(() => {
    if (isEdit) {
      const chartData = data?.map((item) => ({
        id: item.id,
        position: item?.position,
      }));
      position(chartData, true);
    } else {
      dispatch(setIsEdit(!isEdit));
      dispatch({
        type: "alertchange",
        payload: {
          id: "Organisationalchart",
          message: "Another users also edit this business group",
        },
      });
    }
  }, [isEdit, data, position, dispatch]);

  const handleResetClick = useCallback(() => {
    setOpenReset(true);
  }, []);

  const handleCancelClick = useCallback(() => {
    dispatch(setIsEdit(!isEdit));
    dispatch({
      type: "alertremove",
      payload: {
        id: "Organisationalchart",
        message: "Another users also edit this business group",
      },
    });
  }, [dispatch, isEdit]);

  const handleWarningClose = useCallback(() => {
    dispatch(setIsEdit(!isEdit));
    dispatch({
      type: "alertremove",
      payload: {
        id: "Organisationalchart",
        message: "Another users also edit this chart",
      },
    });
  }, [dispatch, isEdit]);

  // Memoize warning condition to prevent unnecessary re-renders
  const shouldShowWarning = useMemo(() => {
    return (
      isEdit &&
      warndata?.users?.filter((item) => item !== admin?._id + "-" + deviceId)
        .length > 0
    );
  }, [isEdit, warndata?.users, admin?._id, deviceId]);

  // Check if we are on the Home page, and trigger the function when navigating away

  return (
    <div className="position-relative" style={containerStyle}>
      {admin?.permissions?.editOrganization && 
      <button
        onClick={handleEditSaveClick}
        style={{
          top: "20px",
          left: "20px",
          zIndex: 1000,
        }}
        className="btn btn-primary position-absolute"
      >
        {isEdit ? "Save" : "Edit"}
      </button>}
      {!isEdit && admin?.permissions?.editOrganization && (
        <button
          onClick={handleResetClick}
          style={{
            top: "20px",
            left: "90px",
            zIndex: 1000,
          }}
          className="btn btn-outline-primary position-absolute"
        >
          Reset
        </button>
      )}
      {isEdit && (
        <button
          onClick={handleCancelClick}
          style={{
            top: "20px",
            left: "90px",
            zIndex: 1000,
          }}
          className="btn btn-outline-primary position-absolute"
        >
          Cancel
        </button>
      )}
      {!isEdit && <D3_View fitOnMountKey={fitOnMountKey} />}
      {isEdit && (
        <Suspense fallback={<LoadingOverlay open />}>
          <D3Edit setdata={setdata} isEdit={isEdit} />
        </Suspense>
      )}
      {shouldShowWarning && (
        <WarningModal
          setOpen={handleWarningClose}
          setNodeData={setdata}
          users={warndata?.users}
          nodeId={"Organisationalchart"}
        />
      )}

      {isPositionsUpdating && <LoadingOverlay open />}
      <ResetChartModal
        onReset={resetchart}
        open={openReset}
        setOpen={setOpenReset}
      />
    </div>
  );
}

// Memoize FullLayout to prevent unnecessary re-renders
export const FullLayout = React.memo(FullLayoutComponent);
